import type { Command } from './interfaces/command.interface.js';

export interface HistoryOptions {
  /** Maximum number of commands kept on the undo stack. 0 = unlimited. Default: 100. */
  limit?: number;
}

export type HistoryChangeListener = () => void;

/**
 * Undo/redo stack for `Command<TState>` instances.
 *
 * The state object lives outside; this class only orchestrates which command
 * runs when. Callers pass their state into `push`, `undo`, `redo`.
 */
export class History<TState = unknown> {
  private readonly undoStack: Command<TState>[] = [];
  private readonly redoStack: Command<TState>[] = [];
  private readonly listeners = new Set<HistoryChangeListener>();
  private readonly limit: number;

  constructor(options: HistoryOptions = {}) {
    this.limit = options.limit ?? 100;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoSize(): number {
    return this.undoStack.length;
  }

  get redoSize(): number {
    return this.redoStack.length;
  }

  /** Apply the command and push it onto the undo stack. Clears the redo stack. */
  push(command: Command<TState>, state: TState): void {
    command.apply(state);

    const top = this.undoStack[this.undoStack.length - 1];
    const merged = top?.merge ? top.merge(command) : null;
    if (merged) {
      this.undoStack[this.undoStack.length - 1] = merged;
    } else {
      this.undoStack.push(command);
      if (this.limit > 0 && this.undoStack.length > this.limit) {
        this.undoStack.shift();
      }
    }

    if (this.redoStack.length > 0) {
      this.redoStack.length = 0;
    }
    this.emitChange();
  }

  /** Revert the topmost undoable command. No-op when the undo stack is empty. */
  undo(state: TState): boolean {
    const command = this.undoStack.pop();
    if (!command) return false;
    command.revert(state);
    this.redoStack.push(command);
    this.emitChange();
    return true;
  }

  /** Re-apply the topmost redoable command. No-op when the redo stack is empty. */
  redo(state: TState): boolean {
    const command = this.redoStack.pop();
    if (!command) return false;
    command.apply(state);
    this.undoStack.push(command);
    this.emitChange();
    return true;
  }

  /** Drop both stacks. Does NOT touch the state itself. */
  clear(): void {
    const hadContent = this.undoStack.length > 0 || this.redoStack.length > 0;
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    if (hadContent) this.emitChange();
  }

  /** Subscribe to any change of the undo/redo stacks. Returns an unsubscribe fn. */
  onChange(listener: HistoryChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const listener of this.listeners) listener();
  }
}
